import type * as React from 'react';

import {
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_CONTENT_REF,
    BLOCK_DIFF,
    BLOCK_DIFF_UNMODIFIED,
    BLOCK_DIVIDER,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_MATH,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABLE,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
    BLOCK_UPDATES,
    INLINE_ANNOTATION,
    INLINE_BUTTON,
    INLINE_EXPRESSION,
    INLINE_ICON,
    INLINE_IMAGE,
    INLINE_LINK,
    INLINE_MATH,
    MARK_BOLD,
    MARK_CODE,
    MARK_COLOR,
    MARK_DIFF_ADDED,
    MARK_DIFF_DELETED,
    MARK_DIFF_MODIFIED,
    MARK_ITALIC,
    MARK_KEYBOARD,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
} from '@gitbook/doc-core';
import { Block, Document, Inline } from '@gitbook/slate';
import type {
    BlockProps,
    MarkProps,
    NodeProps,
    Plugin,
    VirtualNodeProps,
} from '@gitbook/slate-react';

import { InlineAnnotation } from './annotation';
import { InlineButton } from './buttons';
import { BlockCode, BlockCodeLine } from './code';
import { VirtualBlockCodeLine } from './code/VirtualBlockCodeLine';
import { BlockColumn, BlockColumns } from './columns';
import { getBlockEstimatedHeight, getBlockSpacing } from './common';
import { BlockContentRef } from './content-refs';
import { BlockDiff, BlockDiffUnmodified, MarkDiff } from './diff';
import { BlockDivider } from './divider';
import { BlockDrawing } from './drawing';
import { BlockEmbed, BlockIntegration } from './embed-integration';
import { BlockExpandable } from './expandable';
import { InlineExpression } from './expression';
import { BlockFile } from './files';
import { BlockHeading, VirtualBlockHeading } from './heading';
import { InlineIcon } from './icon';
import { BlockIf } from './if';
import { BlockImage, BlockImages, InlineImage } from './images';
import { InlineLink } from './link';
import { BlockList, BlockListItem } from './list';
import {
    MarkBold,
    MarkCode,
    MarkColor,
    MarkItalic,
    MarkKeyboard,
    MarkStrikethrough,
    MarkSubscript,
    MarkSuperscript,
} from './marks';
import { BlockMath, InlineMath } from './math';
import {
    BlockOpenAPI,
    BlockOpenAPIOperation,
    BlockOpenAPISchemas,
    VirtualBlockOpenAPI,
} from './openapi';
import { BlockOpenAPIWebhook } from './openapi/BlockOpenAPIWebhook';
import { BlockParagraph } from './paragraph';
import { BlockHint, BlockQuote } from './quote-hint';
import { BlockReusableContent } from './reusable-content';
import { BlockStepper, BlockStepperStep } from './stepper';
import { BlockTable } from './table';
import { BlockTabs, BlockTabsItem } from './tabs';
import { VirtualBlockTabsItem } from './tabs/VirtualBlockTabsItem';
import { BlockUpdate, BlockUpdates } from './updates';

/**
 * Main plugin
 */
export const mainPlugin: Plugin = {
    renderNode,
    renderVirtualNode,
    renderMark,
    estimateNodeSize: (node, parent) => {
        const spacing = getBlockSpacing(node, parent);
        return { height: getBlockEstimatedHeight(node, parent, { blockSpacing: spacing }) };
    },
};

/**
 * Rendering method for blocks.
 */
export function renderNode(props: NodeProps): React.ReactNode {
    if (Block.isBlock(props.node) && !Inline.isInline(props.parent)) {
        const blockProps = props as BlockProps;
        switch (props.node.type) {
            //
            // Blocks
            //
            case BLOCK_PARAGRAPH:
                return <BlockParagraph {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_LIST_UL:
            case BLOCK_LIST_OL:
            case BLOCK_LIST_TASKS:
                return <BlockList {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_LIST_ITEM:
                return <BlockListItem {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_HEADING_1:
            case BLOCK_HEADING_2:
            case BLOCK_HEADING_3:
                return <BlockHeading {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_TABLE:
                return <BlockTable {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_CODE:
                return <BlockCode {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_CODE_LINE:
                return <BlockCodeLine {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_IMAGES:
                return <BlockImages {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_IMAGE:
                return <BlockImage {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_FILE:
                return <BlockFile {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_EMBED:
                return <BlockEmbed {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_OPENAPI:
                return <BlockOpenAPI {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_OPENAPI_OPERATION:
                return (
                    <BlockOpenAPIOperation
                        {...blockProps}
                        node={props.node}
                        parent={props.parent}
                    />
                );
            case BLOCK_OPENAPI_SCHEMAS:
                return (
                    <BlockOpenAPISchemas {...blockProps} node={props.node} parent={props.parent} />
                );
            case BLOCK_OPENAPI_WEBHOOK:
                return (
                    <BlockOpenAPIWebhook {...blockProps} node={props.node} parent={props.parent} />
                );
            case BLOCK_HINT:
                return <BlockHint {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_QUOTE:
                return <BlockQuote {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_IF:
                return <BlockIf {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_TABS:
                return <BlockTabs {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_TABS_ITEM:
                return <BlockTabsItem {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_STEPPER:
                return <BlockStepper {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_STEPPER_STEP:
                return <BlockStepperStep {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_UPDATES:
                return <BlockUpdates {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_UPDATE:
                return <BlockUpdate {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_MATH:
                return <BlockMath {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_DIFF:
                return <BlockDiff {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_DIFF_UNMODIFIED:
                return (
                    <BlockDiffUnmodified {...blockProps} node={props.node} parent={props.parent} />
                );
            case BLOCK_CONTENT_REF:
                return <BlockContentRef {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_REUSABLE_CONTENT:
                return (
                    <BlockReusableContent {...blockProps} node={props.node} parent={props.parent} />
                );
            case BLOCK_EXPANDABLE:
                return <BlockExpandable {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_DRAWING:
                return <BlockDrawing {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_INTEGRATION:
                return <BlockIntegration {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_DIVIDER:
                return <BlockDivider {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_COLUMNS:
                return <BlockColumns {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_COLUMN:
                return <BlockColumn {...blockProps} node={props.node} parent={props.parent} />;
        }
    }

    if (Inline.isInline(props.node) && !Document.isDocument(props.parent)) {
        switch (props.node.type) {
            //
            // Inlines
            //
            case INLINE_LINK:
                return <InlineLink {...props} node={props.node} parent={props.parent} />;
            case INLINE_IMAGE:
                return <InlineImage {...props} node={props.node} parent={props.parent} />;
            case INLINE_MATH:
                return <InlineMath {...props} node={props.node} parent={props.parent} />;
            case INLINE_EXPRESSION:
                return <InlineExpression {...props} node={props.node} parent={props.parent} />;
            case INLINE_ANNOTATION:
                return <InlineAnnotation {...props} node={props.node} parent={props.parent} />;
            case INLINE_BUTTON:
                return <InlineButton {...props} node={props.node} parent={props.parent} />;
            case INLINE_ICON:
                return <InlineIcon {...props} node={props.node} parent={props.parent} />;
        }
    }

    return null;
}

/**
 * Rendering for virtual nodes.
 */
export function renderVirtualNode(props: VirtualNodeProps): React.ReactNode | null {
    switch (props.node.type) {
        case BLOCK_HEADING_1:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3:
            // VirtualBlockHeading needs a key as it is rendered in a list in NodeVirtualRenderer
            return <VirtualBlockHeading {...props} key={props.node.key} />;
        case BLOCK_TABS_ITEM:
            return <VirtualBlockTabsItem {...props} key={props.node.key} />;
        case BLOCK_OPENAPI:
        case BLOCK_OPENAPI_OPERATION:
            return <VirtualBlockOpenAPI {...props} key={props.node.key} />;
        case BLOCK_CODE_LINE:
            return <VirtualBlockCodeLine {...props} key={props.node.key} />;
    }
}

/**
 * Rendering method for marks.
 */
export function renderMark(props: MarkProps): React.ReactNode {
    switch (props.mark.type) {
        case MARK_BOLD:
            return <MarkBold {...props} />;
        case MARK_ITALIC:
            return <MarkItalic {...props} />;
        case MARK_STRIKETHROUGH:
            return <MarkStrikethrough {...props} />;
        case MARK_SUPERSCRIPT:
            return <MarkSuperscript {...props} />;
        case MARK_SUBSCRIPT:
            return <MarkSubscript {...props} />;
        case MARK_CODE:
            return <MarkCode {...props} />;
        case MARK_COLOR:
            return <MarkColor {...props} />;
        case MARK_KEYBOARD:
            return <MarkKeyboard {...props} />;
        case MARK_DIFF_ADDED:
        case MARK_DIFF_MODIFIED:
        case MARK_DIFF_DELETED:
            return <MarkDiff {...props} />;
    }

    return null;
}
